import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const tdBankCaseStudy: ContentObject = {
    id: 'td-bank-genai-workflows',
    kind: 'system',
    type: 'case-study',
    name: 'GitHub Copilot & GenAI Accelerate Workflows at TD Bank',
    description:
        'TD Bank pilots GitHub Copilot, finding 50% of engineers could save up to 20 hours per sprint, with 93% feeling equally or more productive, boosting efficiency and focusing engineers on complex tasks.',
    url: 'https://www.newswire.ca/news-releases/td-launches-new-generative-ai-pilots-to-help-empower-colleagues-to-deliver-legendary-customer-experiences-806595494.html',
    scores: createScores({
        detailedMetrics: 8.0,
        relevanceToUseCase: 6.0,
        reliabilityOfSource: 10.0,
        strategicAndTacticalInformation: 7.0,
        technicalImplementationDetails: 4.0,
        noVendorBias: 7.0,
        relevanceToClient: 10.0,
    }),
    publishedAt: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000), // 16 days ago
    markdown: `# 1. Who the Source of Information Is 
 
 Source: The case study is based on a press release from TD Bank Group published May 16, 2024, as reported by CNW Group (Newswire). TD Bank Group is a major North American financial institution and the information has been provided directly by its leadership along with insights from its internal innovation teams. 
 
 Relevant Quote: 
 > ""Generative AI is transformative; its potential to revolutionize how we deliver for our colleagues and customers is incredible,"" said Luke Gee, Head of AI and Analytics at TD.
 
 # 2. Successes 
 
 TD Bank Group has achieved notable successes with its generative AI pilot for its engineering practice, specifically through the implementation of GitHub Copilot. Among the key accomplishments: 
 
 - Accelerated coding workflows by offering real time suggestions for code completion and testing. 
 - Empowered TD engineers to focus on complex problems by automating simpler tasks, thereby boosting overall productivity. 
 - Pilot survey results indicated strong positive feedback among engineers:
   - 50% of engineers reported that GitHub Copilot could save them up to 20 hours over a two-week sprint. 
   - 93% of data engineers felt equally or more productive when using the tool. 
   - 75% of engineers said that integrating GitHub Copilot into their daily work was easy. 
 
 Relevant Quote: 
 > ""We expect that generative AI is going play an important role in how we evolve our engineering function and culture at TD,"" said Licenia Rojas, Senior Vice President, Chief Engineer and Architect at TD. This underscores the bank's confidence that the pilot's success will lead to broader application and further efficiency gains.
 
 # 3. Challenges 
 
 While the press release does not list extensive specifics on the obstacles encountered during the pilot, TD Bank Group is clearly mindful of potential challenges associated with integrating generative AI in a highly regulated, security-sensitive environment. Areas of possible concern include: 
 
 - Ensuring security and safeguarding both customer data and business interests. 
 - Seamlessly integrating a new tool into established developer workflows without disrupting ongoing projects. 
 - Managing the transition in skillsets and ensuring that engineers could appropriately harness AI suggestions while maintaining code quality. 
 
 Relevant Quote: 
 > ""We are taking a thoughtful approach that prioritizes security to help protect our customers and business,"" said Licenia Rojas. This comment highlights that while the benefits are significant, the bank is cautious in addressing any potential risks and integration challenges.
 
 # 4. Technical Details 
 
 In its effort to accelerate the pace of software development, TD Bank Group launched a pilot focused on integrating GitHub Copilot into developers' environments. Key technical details include: 
 
 - GitHub Copilot is a generative AI programming assistant developed by Microsoft. 
 - The tool analyzes code in real time and provides context-aware suggestions such as code completions, testing scaffolds, and security recommendations. 
 - Developers can interact with Copilot using natural language prompts to retrieve explanations of complex coding topics or to prompt it into suggesting code snippets that meet specific functional criteria. 
 - During the pilot, GitHub Copilot was used to assist with generating unit testing code, thereby reducing manual effort and accelerating the overall development cycle. 
 
 Relevant Quote: 
 > ""GitHub Copilot is designed to help streamline simple tasks and accelerate code development. Users can prompt Copilot in natural language to get responses to coding questions,"" as the solution facilitates an efficient and streamlined developer experience.
 
 # 5. Results 
 
 The pilot has demonstrated tangible productivity and efficiency gains among TD's engineering teams: 
 
 - A survey taken between September and December 2023 among a select group of engineers revealed:
   - 50% of participants noted they could save up to 20 hours over a two-week sprint.
   - 93% of data engineers stated they were as productive or more productive with the assistance of GitHub Copilot.
   - 75% of participating engineers found it easy to integrate GitHub Copilot into their everyday work. 
 
 These metrics point to a strong positive impact on both the speed and quality of code development. The pilot's success indicates that the tool is helping reduce routine overhead—freeing up developers to focus on more complex, value-added tasks.
 
 Relevant Quote: 
 > ""In a survey of colleagues who participated in the test, TD found that 93% of data engineers said they were equally or more productive with the help of GitHub Copilot,"" reinforcing the pilot's role in enhancing developer performance.
 
 # 6. Vendors Used 
 
 TD Bank Group's initiative leverages strategic partnerships and in-house expertise: 
 
 - Primary Vendor for software development productivity: GitHub Copilot, a tool developed by Microsoft. This vendor provides the generative AI programming assistant that has been integrated into TD's engineering workflows.
 - Additionally, on the contact centre side (which, though not directly related to software developer productivity, highlights the bank's broader adoption of generative AI) TD's AI research centre, Layer 6, played a key role in developing the generative AI virtual assistant.
 
 Relevant Quote: 
 > ""By implementing solutions such as GitHub Copilot, we're helping empower our talented engineers to focus on new opportunities,"" said Licenia Rojas, underscoring the importance of this vendor in the bank's strategy for deepening AI integration in software development.
 
 # Conclusion 
 
 TD Bank Group's generative AI pilot for its engineering team illustrates how combining cutting-edge tools like GitHub Copilot with a robust innovation approach can yield significant productivity gains. With measurable improvements in time savings, productivity, and ease of integration, the project demonstrates a promising future for enhancing software development workflows. At the same time, the bank's mindful approach to security and integration challenges reflects a mature understanding of both the opportunities and responsibilities that come with deploying AI in a mission-critical environment.`,
    references: [
        {
            id: '1',
            name: 'TD Launches New Generative AI Pilots to Help Empower Colleagues to Deliver Legendary Customer Experiences',
            author: 'TD Bank Group (via CNW Group/Newswire)',
            year: '2024',
            publisher: 'Newswire.ca',
            url: 'https://www.newswire.ca/news-releases/td-launches-new-generative-ai-pilots-to-help-empower-colleagues-to-deliver-legendary-customer-experiences-806595494.html',
        },
    ],
};
